import React, { useEffect, useState } from 'react';
import { Button, Col, Modal, Row } from 'react-bootstrap';
import styles from 'views/MockServices/Tabs/MockServices/MockServiceMarSimpleDetail.module.css';
import { useTranslation } from 'react-i18next';
import { MockService } from '../../../../types/rest/mockServiceTypes';
import { useIsMount } from '../../../../hooks/useIsMountHook';
import BzmIcon from '../../../../components/icons/BzmIcon';
import clsx from 'clsx';
import MsgConnectionTable from './MsgConnectionTable';
import _ from 'lodash';
import { MsgComponent } from '../../../../types/rest/assetCatalogTypes';
import { showToastNotification } from '../../../../store/ui/toasts/toastsActions';
import { ToastType } from '../../../../store/ui/toasts/toastsTypes';
import { getErrorMessage } from '../../../../utils/miscUtil';
import { useDispatch } from 'react-redux';
import {
  importTopicsAndQueues,
  testMessagingMockConnection,
} from '../../../../store/mockServices/mockServiceList/mockServiceListActions';
import SimpleLoader from '../../../../components/loaders/SimpleLoader';

interface MockServiceMarSimpleDetailProps {
  mockService: MockService;
  closeModalHandler: () => void;
}

const MsgTestConnectionModal = ({ mockService, closeModalHandler }: MockServiceMarSimpleDetailProps) => {
  const { t } = useTranslation('MockServices');
  const dispatch = useDispatch();

  const [connectionError, setConnectionError] = useState<boolean>(false);
  const [logDetails, setSLogDetails] = useState<string>('');
  const isMount = useIsMount();

  const [selectedQueues, setSelectedQueues] = useState<string[]>([]);
  const [selectedTopics, setSelectedTopics] = useState<string[]>([]);
  const [topics, setTopics] = useState<MsgComponent[]>([]);
  const [queues, setQueues] = useState<MsgComponent[]>([]);

  const getLogFromConnection = async () => {
    return await testMessagingMockConnection(mockService);
  };

  const logModalClose = async (importSelected?: boolean) => {
    if (importSelected) {
      const queuesToImport = selectedQueues.map((queue: string) => {
        return { name: queue };
      });
      const topicsToImport = selectedTopics.map((topic: string) => {
        return { name: topic, isDurable: false, durableSubscriptionName: null };
      });

      try {
        await dispatch(importTopicsAndQueues(mockService, queuesToImport, topicsToImport));
      } catch (e) {
        dispatch(
          showToastNotification(
            ToastType.Danger,
            'There was a problem when importing topics and queues',
            getErrorMessage(e),
          ),
        );
      }
    }
    closeModalHandler();
  };

  useEffect(() => {
    if (isMount) {
      getLogFromConnection()
        .then(response => {
          setTopics(response.topics ? response.topics : []);
          setQueues(response.queues ? response.queues : []);
          setSLogDetails('Connection successful. Log details fetched.');
        })
        .catch(error => {
          setSLogDetails(error.message || 'Failed to fetch log details.');
          setConnectionError(true);
        });
    }
  }, []);

  const getModalBodyContent = () => {
    return (
      <>
        <Row className={'mb-4'}>
          <Col>
            <div
              className={clsx(
                'p-4 d-flex align-items-center',
                connectionError ? styles.errorLight : styles.successLight,
              )}
            >
              <BzmIcon
                variant={connectionError ? 'icon-bzm-danger' : 'icon-bzm-ct-check-circle-solid'}
                className={`icon-sm ${connectionError ? 'icon-theme-red' : 'icon-theme-green'}`}
              />
              <span className="ml-1">{connectionError ? 'Connection Failed' : 'Successfully Connected'}</span>
            </div>
          </Col>
        </Row>
        <Row className={'mb-3'}>
          <Col>
            <div className="p-4 bg-light border border-grey">
              <div className="mb-3">
                <span className="font-weight-semi-bold"> Log Details </span>{' '}
              </div>
              <p>
                <span
                  dangerouslySetInnerHTML={{
                    __html: logDetails.replace(/\n/g, '<br />'),
                  }}
                />
              </p>
            </div>
          </Col>
        </Row>
        {!connectionError && (
          <>
            <Row>
              <div className="p-4">
                <div className="mb-4 font-weight-semi-bold"> Select Queues </div>
                <MsgConnectionTable
                  headerText={'Queues'}
                  msgComponentList={queues}
                  onSelectionChange={(selectedItems: Array<any>) =>
                    setSelectedQueues(selectedItems.map((item: any) => item.name))
                  }
                  isLoading={false}
                  page={1}
                  totalElements={queues.length}
                  noResultsText={'No queues found.'}
                />
              </div>
            </Row>
            <Row>
              <div className="p-4">
                <div className="mb-4 font-weight-semi-bold">Select Topics</div>
                <MsgConnectionTable
                  headerText={'Topics'}
                  msgComponentList={topics}
                  onSelectionChange={(selectedItems: Array<any>) =>
                    setSelectedTopics(selectedItems.map((item: any) => item.name))
                  }
                  isLoading={false}
                  page={1}
                  totalElements={queues.length}
                  noResultsText={'No topics found.'}
                />
              </div>
            </Row>
          </>
        )}
      </>
    );
  };

  return (
    <Modal backdrop={true} show={true} onHide={() => logModalClose()} size={'lg'}>
      <Modal.Header>
        <div className="d-inline-flex w-100 justify-content-between">
          <div id={`mockServiceLogsTitle`} className="font-size-xlg font-weight-bold">
            Test Connection
          </div>

          <button
            type="button"
            id={`mockServiceLogsCloseButton`}
            title={t('common:actions.close')}
            className="close pt-0"
            aria-label={t('common:actions.close')}
            onClick={() => logModalClose()}
          >
            <span aria-hidden="true">&times;</span>
          </button>
        </div>
      </Modal.Header>

      <Modal.Body>{isMount ? <SimpleLoader /> : getModalBodyContent()}</Modal.Body>
      <Modal.Footer className="text-right">
        {connectionError ? (
          <div>
            <Button
              variant="primary"
              id={`TestCloseButton`}
              title={t('common:actions.close')}
              onClick={() => logModalClose()}
            >
              Close
            </Button>
          </div>
        ) : (
          <div>
            <Button
              id={`TestCancelButton`}
              className="btn btn-link"
              title={t('common:actions.cancel')}
              onClick={() => logModalClose()}
              variant="link"
            >
              {t('common:actions.cancel')}
            </Button>
            <Button
              disabled={_.isEmpty(selectedQueues) && _.isEmpty(selectedTopics)}
              variant="primary"
              id={`TestImportButton`}
              title={'Import Queues and Topics'}
              onClick={() => logModalClose(true)}
            >
              Import Selected
            </Button>
          </div>
        )}
      </Modal.Footer>
    </Modal>
  );
};

export default MsgTestConnectionModal;
